import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { cssVar } from "../utils/cssVar";

/** Border color of the checkbox in its unchecked state. Exported so sibling
 *  row controls (the list row's kebab) can match it. */
export const UNCHECKED_BORDER_COLOR = "#999999";

export type SelectionCheckboxProps = {
  checked: boolean;
  /** Toggles the checked state. Pointer events are stopped before this fires. */
  onChange: () => void;
  /** Edge length in px. Defaults to 16. */
  size?: number;
  /**
   * Name of the item this checkbox belongs to. Announced after the action
   * ("Select holiday.png"), so the accessible name says what the click does
   * instead of repeating the file name the card already carries.
   */
  itemName?: string;
  /**
   * Overrides the hover tooltip. Defaults to the state-aware "Select" /
   * "Deselect", so the label always names the action the click performs.
   */
  tooltip?: string;
  /** Tab order of the underlying button. Pass -1 to skip it (default 0). */
  tabIndex?: number;
};

/**
 * Selection checkbox used by the file card and the list row.
 *
 * Matches the `fileCardSelectionBox` XD spec: a 2px-radius square with a white
 * fill and a 1px gray inner-stroke border when unchecked, and a primary-color
 * fill with a white check when checked.
 *
 * Implemented as a native button (not MUI's Checkbox) so the unchecked
 * bordered-square appearance is exact and there is no ripple/material chrome.
 * Pointer events are stopped so toggling never bubbles to the card's
 * click/preview handler or the marquee drag.
 *
 * Always carries a hover tooltip naming the action its click performs
 * ("Select" / "Deselect"), so a checked box never invites the user to select
 * what is already selected. Capitalized, and "Deselect" rather than "unselect",
 * like the neighbouring labels and like the platforms (macOS, Windows, Drive).
 */
export function SelectionCheckbox({
  checked,
  onChange,
  size = 16,
  itemName,
  tooltip,
  tabIndex,
}: SelectionCheckboxProps) {
  const { t } = useTranslation();
  const primaryColor = cssVar("--primary-color");
  // The tooltip names what the click does, so a checked box offers "Deselect".
  const title = tooltip ?? (checked ? t("Deselect") : t("Select"));
  // The tooltip alone never reaches assistive tech: an aria-label wins over it
  // for the accessible name, so both are derived from the same state.
  const label = itemName ? `${title} ${itemName}` : title;

  return (
    <Tooltip title={title} placement="top">
      <button
        type="button"
        role="checkbox"
        aria-checked={checked}
        aria-label={label}
        tabIndex={tabIndex}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onChange();
        }}
        className="selection-checkbox flex shrink-0 items-center justify-center outline-none transition-colors"
        style={{
          width: size,
          height: size,
          borderRadius: 2,
          border: `1px solid ${
            checked ? primaryColor : UNCHECKED_BORDER_COLOR
          }`,
          backgroundColor: checked ? primaryColor : "#FFFFFF",
        }}
      >
        {checked && (
          <FontAwesomeIcon
            icon={faCheck}
            style={{ color: "#FFFFFF", fontSize: Math.round(size * 0.6) }}
          />
        )}
      </button>
    </Tooltip>
  );
}
